import * as React from 'react';
import * as _ from 'lodash';
import { Dispatch, connect } from 'react-redux';
import { IApplicationState, AppActionCreators, IAppState, IMicrositeResponse, MicrositeActionCreators } from '@nkr/store/index';
import { IBaseResponse, IMicrositeRecipientImage } from '@nkr/models/index';
import { Alert, Button, Col, ControlLabel, FormControl, FormGroup, Row, Well } from 'react-bootstrap';
import { AxiosError } from 'axios';
import { toast } from 'react-toastify';

interface IImageWithValidation extends IMicrositeRecipientImage {
    errorList: string[]
}

interface IProps {
    micrositeId: number,
    centerId: number,
    isAdmin: boolean,
    previousUrl: string,
}

interface IStateProps {
    app: IAppState
}

interface IDispatchProps {
    showLoading: (message: string) => Promise<void>,
    hideLoading: () => void,
    getMicrosite: (id: number, forCoach: boolean) => Promise<IMicrositeResponse>,
    saveProfile: (id: number, patientStoryPending: string, patientWhyPending: string, images: { displayOrder: number, caption: string }[], initials: string) => Promise<IBaseResponse>
}

type TProps = IProps & IStateProps & IDispatchProps;

interface IState {
    micrositeTypeId: number,
    micrositeType: string,
    firstName: string,
    lastName: string,
    email: string,
    birthdate: string,
    patientStory: string,
    patientWhy: string,
    images: IImageWithValidation[],
    initials: string,
    previousLocation: string,
    storyErrorList: string[],
    whyErrorList: string[],
    initialsValid: boolean,
    dirty: boolean
}

class MicrositeProfile extends React.Component<TProps, IState> {
    readonly state: IState = {
        micrositeTypeId: 0,
        micrositeType: '',
        firstName: '',
        lastName: '',
        email: '',
        birthdate: '',
        patientStory: '',
        patientWhy: '',
        images: [],
        initials: '',
        previousLocation: '',
        storyErrorList: [],
        whyErrorList: [],
        initialsValid: true,
        dirty: false
    }

    componentDidMount = () => {
        this.getData();
    }

    getData = () => {
        let previousLocation = this.props.isAdmin ? `/admin/microsite/${this.props.previousUrl}` : `/center/${this.props.centerId}/microsite/${this.props.previousUrl}`;

        this.props.showLoading('Getting the microsite, one moment please...').then(() => {
            this.props.getMicrosite(this.props.micrositeId, false)
                .then(response => {
                    if (response.data.success) {
                        let { micrositeTypeId, micrositeType, firstName, lastName, email, birthdate, patientStoryPending: patientStory, patientWhyPending: patientWhy, imagesPending: images } = response.data.results;

                        this.setState({
                            micrositeTypeId,
                            micrositeType,
                            firstName,
                            lastName,
                            email,
                            birthdate,
                            patientStory,
                            patientWhy,
                            images: images.map(image => { return { ...image, caption: (image.caption != null ? image.caption : ''), errorList: [] } }),
                            previousLocation
                        });
                    }

                    this.props.hideLoading();
                })
                .catch((error: AxiosError) => {
                    this.props.hideLoading();
                });
        });
    }

    handleStoryChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        this.setState({
            patientStory: e.currentTarget.value
        });
    }

    handleWhyChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        this.setState({
            patientWhy: e.currentTarget.value
        });
    }

    handleCaptionChange = (index: number, caption: string) => {
        let images = _.cloneDeep(this.state.images).map(image => {
            if (image.displayOrder == index) {
                image.caption = caption;
            }

            return image;
        });

        this.setState({
            images
        });
    }

    handleInitialsChange = (e: React.FormEvent<FormControl & HTMLInputElement>) => {
        this.setState({
            initials: e.currentTarget.value
        });
    }

    handleSubmit = () => {
        let { storyErrorList, whyErrorList, validatedImages: images, initialsValid } = this.validate();

        this.setState({
            storyErrorList,
            whyErrorList,
            images,
            initialsValid,
            dirty: true
        }, () => {
            if (!this.isValid()) {
                window.scrollTo(0, 0);
            } else {
                let { patientStory, patientWhy, images, initials } = this.state;

                let captions = images.map(item => {
                    return { displayOrder: item.displayOrder, caption: item.caption };
                });

                this.props.showLoading('Saving profile, one moment please...').then(() => {
                    this.props.saveProfile(this.props.micrositeId, patientStory, patientWhy, captions, initials)
                        .then(response => {
                            this.props.hideLoading();

                            if (response.data.success) {
                                toast.success('Microsite profile updated successfully, going back to previous page', {
                                    onClose: () => {
                                        window.location.href = this.state.previousLocation;
                                    }
                                });
                            }
                        })
                        .catch((error: AxiosError) => {
                            toast.error('Unable to deny the microsite, the following error has been recorded: ' + error.message);
                        });
                });
            }
        });
    }

    handleCancel = () => {
        window.location.href = this.state.previousLocation;
    }

    // range = (a: number, b: number) => {
    //     return Array(Math.abs(a - b) + 1).fill(a).map(function (v, i) {
    //         return v + i * (a > b ? -1 : 1)
    //     });
    // }

    containsHtml = (value: string) => {
        let temp = document.createElement('div');
        temp.innerHTML = value;

        let containsHtml = temp.children.length > 0;

        return containsHtml;
    };

    containsEmoji = (value: string) => {
        // let charRange = this.range(32, 127);
        // let charsAllowed = [10, 13, 45, 160, 8211, 8216, 8217, 8220, 8221].concat(charRange); // common special chars from Word: hyphon, single quotes, double quotes

        // let emojis = value.split('').filter(function (letter) {
        //     return charsAllowed.indexOf(letter.charCodeAt(0)) < 0;
        // });

        // return emojis.length > 0;

        return false;
    };

    validate = () => {
        let { patientStory, patientWhy, images, initials } = this.state;

        let storyErrorList: string[] = [];
        let whyErrorList: string[] = [];

        let storyMissing = patientStory.length === 0;

        if (storyMissing) {
            storyErrorList.push('The patient story is required.');
        }

        let storyTooLong = patientStory.length > __MICROSITE_STORY_CHAR_LIMIT__;

        if (storyTooLong) {
            storyErrorList.push('Your story is too long, please limit your story to ' + __MICROSITE_STORY_CHAR_LIMIT__ + ' characters.');
        }

        let storyContainsHtml = this.containsHtml(patientStory);

        if (storyContainsHtml) {
            storyErrorList.push('Your story contains HTML which is not allowed, please limit your story to text only.');
        }

        let storyContainsEmoji = this.containsEmoji(patientStory);

        if (storyContainsEmoji) {
            storyErrorList.push('Your story contains unapproved characters, such as an emoji.');
        }

        let whyTooLong = patientWhy.length > __MICROSITE_WHY_CHAR_LIMIT__;

        if (whyTooLong) {
            whyErrorList.push('The "why I need a ' + this.state.micrositeType + '" section is too long, please limit it to ' + __MICROSITE_WHY_CHAR_LIMIT__ + ' characters.');
        }

        let whyContainsHtml = this.containsHtml(patientWhy);

        if (whyContainsHtml) {
            whyErrorList.push('"why I need a ' + this.state.micrositeType + '" section contains HTML which is not allowed, please limit it to text only.');
        }

        let whyContainsEmoji = this.containsEmoji(patientWhy);

        if (whyContainsEmoji) {
            whyErrorList.push('"why I need a ' + this.state.micrositeType + '" section contains unapproved characters, such as an emoji.');
        }

        let validatedImages = _.cloneDeep(images).map((image, index) => {
            image.errorList = this.validateCaption(image.caption);
            return image;
        });

        let initialsValid = initials != '';

        return { storyErrorList, whyErrorList, validatedImages, initialsValid };
    };

    validateCaption = (caption: string) => {
        let errorList: string[] = [];

        let captionTooLong = caption.length > __MICROSITE_CAPTION_CHAR_LIMIT__;

        if (captionTooLong) {
            errorList.push('The caption is too long, please limit the caption to ' + __MICROSITE_CAPTION_CHAR_LIMIT__ + ' characters.');
        }

        let captionContainsHtml = this.containsHtml(caption);

        if (captionContainsHtml) {
            errorList.push('HTML is not allowed, please limit the caption to text only.');
        }

        let captionContainsEmoji = this.containsEmoji(caption);

        if (captionContainsEmoji) {
            errorList.push('Emojis are not allowed, please limit the caption to text only.');
        }

        return errorList;
    };

    isValid = () => {
        let { storyErrorList, whyErrorList, images, initialsValid } = this.state;

        let imagesValid = images.filter(x => x.errorList.length > 0);

        return storyErrorList.length == 0 && whyErrorList.length == 0 && imagesValid.length == 0 && initialsValid;
    }

    render = () => {
        let storyCharsRemmaining = __MICROSITE_STORY_CHAR_LIMIT__ - this.state.patientStory.length;
        let whyCharsRemaining = __MICROSITE_WHY_CHAR_LIMIT__ - this.state.patientWhy.length;
        let isValid = this.isValid();

        return (
            <Row>
                <Col xs={12} md={8} mdOffset={2}>
                    {this.state.dirty && !isValid && (
                        <Alert bsStyle='danger'>
                            {this.state.storyErrorList.length > 0 && (
                                <div>
                                    <div style={{ marginBottom: 5 }}>Issues with the story...</div>
                                    <ul style={{ paddingLeft: 20 }}>
                                        {this.state.storyErrorList.map(error => <li>{error}</li>)}
                                    </ul>
                                </div>
                            )}

                            {this.state.whyErrorList.length > 0 && (
                                <div>
                                    <div style={{ marginBottom: 5 }}>Issues with the why section...</div>
                                    <ul style={{ paddingLeft: 20 }}>
                                        {this.state.whyErrorList.map(error => <li>{error}</li>)}
                                    </ul>
                                </div>
                            )}

                            {this.state.initialsValid == false && (
                                <div>
                                    Please enter your initials...
                                </div>
                            )}
                        </Alert>
                    )}

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10 }}>
                        <h1 className='text-left' style={{ margin: 0 }}>Edit Microsite Profile</h1>
                        <div className="dropdown pull-right">
                            <Button bsStyle='link' onClick={this.handleCancel}>Return to Previous List</Button>
                        </div>
                    </div>

                    <br />

                    <Well style={{ display: 'flex', paddingTop: 7.5, paddingBottom: 7.5 }}>
                        <div style={{ display: 'flex', marginRight: 30 }}>
                            <div className='text-bold' style={{ marginRight: 10 }}>First Name:</div>
                            <div>{this.state.firstName}</div>
                        </div>

                        <div style={{ display: 'flex', marginRight: 30 }}>
                            <div className='text-bold' style={{ marginRight: 10 }}>Last Name:</div>
                            <div>{this.state.lastName}</div>
                        </div>

                        <div style={{ display: 'flex', marginRight: 30 }}>
                            <div className='text-bold' style={{ marginRight: 10 }}>Email:</div>
                            <div>{this.state.email}</div>
                        </div>

                        <div style={{ display: 'flex' }}>
                            <div className='text-bold' style={{ marginRight: 10 }}>Date of Birth:</div>
                            <div>{this.state.birthdate}</div>
                        </div>
                    </Well>

                    <FormGroup>
                        <label className='control-label'>
                            Patient's Story
                        </label>
                        <div id='patientStoryParent'>
                            <textarea rows={6} className='form-control' style={{ minHeight: 250 }} value={this.state.patientStory} onChange={this.handleStoryChange} />
                            <div style={{ textAlign: 'right' }}>{storyCharsRemmaining} character(s) remaining...</div>
                        </div>
                    </FormGroup>

                    <FormGroup>
                        <label className='control-label'>
                            Patient's Why I Need a {this.state.micrositeType}
                        </label>
                        <div id='patientWhyParent'>
                            <textarea rows={6} className='form-control' style={{ minHeight: 250 }} value={this.state.patientWhy} onChange={this.handleWhyChange} />
                            <div style={{ textAlign: 'right' }}>{whyCharsRemaining} character(s) remaining...</div>
                        </div>
                    </FormGroup>

                    <FormGroup>
                        <ControlLabel style={{ marginBottom: 15 }}>
                            Images
                        </ControlLabel>
                        <div>
                            {this.state.images.map((image, index) => {
                                let charsRemaining = __MICROSITE_CAPTION_CHAR_LIMIT__ - (image.caption != null ? image.caption.length : 0);
                                let noun = 'character(s)';
                                let imageFilename = image.thumbnail != '' ? image.thumbnail : image.fileName;

                                return (
                                    <div className='col-xs-12 col-lg-3'>
                                        <Well>
                                            <div className="text-center" style={{ height: 215, marginBottom: 5 }}>
                                                <img src={__API__ + '/microsite/image?filename=' + imageFilename} style={{ maxWidth: 235, height: '100%', borderRadius: 4 }} />
                                            </div>

                                            <div>
                                                <textarea
                                                    className='form-control captions'
                                                    rows={5}
                                                    style={{ resize: 'none' }}
                                                    value={image.caption}
                                                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => this.handleCaptionChange(index, e.currentTarget.value)}
                                                />
                                                <div style={{ textAlign: 'right', fontSize: 12 }}>
                                                    {charsRemaining} {noun} remaining...
                                                </div>
                                            </div>
                                        </Well>
                                    </div>
                                );
                            })}
                        </div>
                    </FormGroup>

                    <Alert bsStyle='warning'>
                        <div className='text-center' style={{ marginBottom: 15 }}>
                            By initialing and clicking <strong>Approve Changes</strong> I am stating that I have reviewed these changes<br />with
                            the patient who agrees with the content of this microsite.
                        </div>
                        <div className='text-center' >This will also approve the microsite and send to the NKR for final review.</div>
                    </Alert>


                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginBottom: 10 }}>
                        <FormGroup style={{ margin: '0px 15px 0px 0px', display: 'flex', alignItems: 'center' }} validationState={this.state.dirty && this.state.initialsValid == false ? 'error' : null}>
                            <ControlLabel style={{ marginRight: 5, marginBottom: 0 }}>Initials:</ControlLabel>
                            <FormControl style={{ width: 60 }} title='Your initials' value={this.state.initials} onChange={this.handleInitialsChange} />
                        </FormGroup>

                        <Button bsStyle='primary' bsSize='lg' style={{ marginRight: 5 }} onClick={this.handleSubmit}>Approve Changes</Button>
                        <Button bsStyle='link' onClick={this.handleCancel}>Cancel Changes</Button>
                    </div>

                </Col>
            </Row>
        );
    }
}

const mapStateToProps = (state: IApplicationState): IStateProps => {
    return {
        app: state.app
    };
}

const mapDispatchToProps = (dispatch: Dispatch<any>): IDispatchProps => {
    return {
        showLoading: (message) => {
            return dispatch(AppActionCreators.showLoadingAsync(message));
        },
        hideLoading: () => {
            return dispatch(AppActionCreators.hideLoading());
        },
        getMicrosite: (id, forCoach) => {
            return dispatch(MicrositeActionCreators.getMicrosite(id, forCoach));
        },
        saveProfile: (id, patientStoryPending, patientWhyPending, images, initials) => {
            return dispatch(MicrositeActionCreators.saveProfile(id, patientStoryPending, patientWhyPending, images, initials));
        }
    };
}

export default connect<IStateProps, IDispatchProps, IProps>(
    mapStateToProps,
    mapDispatchToProps)(MicrositeProfile);